import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';

import { EventBroker } from 'BMapsSrc/eventbroker';
import {
    fetchJobs, loadResult, updateJobErrors, removeJob,
} from 'BMapsRedux/jobs/thunks';
import { useDispatch, useSelector } from 'BMapsRedux';
import { lookupJobDefinition, JobResultDestinations, JobStatus } from 'BMapsSrc/jobman';
import { LoadingScreen } from 'BMapsSrc/LoadingScreen';
import { okToClearAndLoad } from 'BMapsSrc/project_data';
import { App } from 'BMapsSrc/BMapsApp';
import BMapsTooltip from 'BMapsUI/BMapsTooltip';
import { Loader } from 'BMapsSrc/Loader';
import { updateLocalJobState } from 'BMapsRedux/jobs';
import DialogContentSection from 'BMapsUI/common/DialogContentSection';
import { getJobState } from 'BMapsRedux/jobs/access';
import LoadingIconButton from 'BMapsUI/common/LoadingIconButton';
import { DialogCancelButton, DialogPrimaryButton } from 'BMapsUI/common/DialogActionButtons';
import { wrapForDisabledTooltip } from 'BMapsUI/common/helpers';
import { userConfirmation } from 'BMapsSrc/utils';
import { setJobsGridTableState } from 'BMapsRedux/projectState/access';
import { ImportErrors } from '../../import_components';

export default function JobsStructuresTab({
    initData, selected, selectivityProps, connection, postLoad,
}) {
    const { jobs, status, localJobState } = useSelector((state) => state.jobs);
    const dispatch = useDispatch();
    const retrieveJobs = () => dispatch(fetchJobs());
    const highlightedJob = initData?.jobid;
    const [flashId, setFlashId] = useState(null);
    const [errors, setErrors] = useState([]);
    const structureJobs = useMemo(
        () => jobs.filter(
            (job) => lookupJobDefinition(job.jobtype)
                ?.resultDestination === JobResultDestinations.STRUCTURE
        ), [jobs]
    );

    const loadJobsList = async () => {
        const { error } = await retrieveJobs();
        if (error) {
            setErrors([error]);
        } else {
            setErrors([]);
        }
    };

    useEffect(() => {
        if (status === 'unloaded') {
            loadJobsList();
        }
    }, [status]);

    // Animates the flash when a new job is created, so that it can be easily visible
    useEffect(() => {
        if (!highlightedJob) return () => null;
        setFlashId(highlightedJob);

        const timer = setTimeout(() => setFlashId(null), 500);
        return () => {
            clearTimeout(timer);
        };
    }, [highlightedJob]);

    return (
        <Box sx={{ overflow: 'hidden' }}>
            {!!selected && (
            <DialogContentSection
                title="Structures from folding jobs"
                style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                contentStyle={{ display: 'flex', flexDirection: 'column' }}
                titleIcon={(
                    <BMapsTooltip title="Refresh Jobs List">
                        <LoadingIconButton Icon={RefreshIcon} load={loadJobsList} />
                    </BMapsTooltip>
            )}
            >
                <JobsGrid
                    jobs={structureJobs}
                    userSpecifiedSelectivity={selectivityProps?.selectivity}
                    dataConnection={connection}
                    flashId={flashId}
                    localJobState={localJobState}
                    cleanup={postLoad}
                    retrieveJobs={retrieveJobs}
                    errors={errors}
                />
            </DialogContentSection>
            )}
        </Box>
    );
}

export function JobsGrid({
    jobs, localJobState, cleanup, flashId, errors,
    userSpecifiedSelectivity, dataConnection, preventMultiSelect,
    setActiveJobs, dataGridProps={},
    columnControl={},
}) {
    const tableState = useSelector((state) => state.projectState.tableState.jobsGridTable);
    const columnWidths = tableState?.columnWidths || {};

    const setColumnWidths = (widths) => {
        setJobsGridTableState({
            columnWidths: widths,
        });
    };
    const [checkedRowItems, setCheckedRowItems] = useState([]);
    const apiRef = useGridApiRef();

    const dispatch = useDispatch();
    const setJobError = (jobid, error) => dispatch(updateLocalJobState({ jobid, error }));
    const loadJob = (jobid, options) => dispatch(loadResult(jobid, options));
    const getErrorMessage = (jobid) => dispatch(updateJobErrors(jobid));
    async function deleteJobs(jobids) {
        const chosenJobs = jobids.map((id) => jobs.find((j) => j.jobid === id));
        const confirmationText = `Are you sure you want to delete the selected job(s)?\n
        ${chosenJobs.map((job) => job.requestmeta?.jobName || job.jobid).join('\n')}\n
        After deletion results will no longer be available.`;
        const ok = await userConfirmation('Delete Selected Job(s)?', confirmationText);
        if (!ok) return;
        for (const jobid of jobids) {
            dispatch(removeJob(jobid));
        }
    }

    // Jobs integration functions.
    async function integrateJobResults(jobids) {
        // Load jobs in the order that they appear in the table.
        const integratingJobs = [];
        for (const id of jobids) {
            const job = jobs.find((j) => j.jobid === id);
            if (job) {
                integratingJobs.push(job);
            }
        }
        if (integratingJobs.length === 0) {
            return;
        }

        // Specific logic for loading new structures
        const firstStructureJob = integratingJobs
            .find(
                (job) => lookupJobDefinition(job.jobtype)
                    ?.resultDestination === JobResultDestinations.STRUCTURE
                && job.state === JobStatus.SUCCEEDED
            );

        if (firstStructureJob
            && userSpecifiedSelectivity === 'individual'
            && App.Workspace.needPermissionToClear()
        ) {
            if (!await okToClearAndLoad()) {
                return;
            }
        }
        const jobsWithOptions = integratingJobs.map((job) => {
            /**
             * @todo This should probably call a new JobDefinition.getIntegrateOptions, each job
             * needing to know the user specified selectivity and whether it is the first one.
             */
            let options;
            const jobDefinition = lookupJobDefinition(job.jobtype);
            if (jobDefinition?.resultDestination === JobResultDestinations.STRUCTURE) {
                const loadOptions = {
                    // The first structure job uses the user-specified selectivity mode,
                    // the rest will be added with "multiProtein" (using the same bfd-server).
                    keepExisting: job === firstStructureJob
                        ? userSpecifiedSelectivity !== 'individual'
                        : true,
                };
                options = { dataConnection, loadOptions };
            }
            return [job, options];
        });
        // End specific logic for loading new structures

        const jobErrors = [];
        await EventBroker.withIgnoredEvents('escapeKey', (async () => {
            for (const [job, options] of jobsWithOptions) {
                const { error } = await LoadingScreen.withLoadingScreen(
                    loadJob(job.jobid, options),
                    `Integrating ${job.jobtype} job results`,
                );
                if (error) {
                    setJobError(job.jobid, error);
                    jobErrors.push({ job, error });
                }
            }
        })()); // IIFE since withIgnoredEvents needs a promise, not a function
        if (setActiveJobs) {
            const activeJobs = integratingJobs.filter((job) => (
                job.state === JobStatus.SUCCEEDED
                && !jobErrors.find((je) => je.job === job)
            ));
            if (activeJobs.length > 0) {
                setActiveJobs(activeJobs);
            }
        }
        if (jobErrors.length === 0) {
            setCheckedRowItems([]);
            if (cleanup) cleanup();
        }
    }

    // building the columns and rows for the data grid
    const { columns, rows } = buildJobsGrid(
        jobs, getErrorMessage, integrateJobResults, deleteJobs, columnControl, columnWidths,
    );

    // Additional data grid props for using multi job intergration
    const allowMultiSelect = Loader.AllowLabFeatures && !preventMultiSelect;
    let multiLoadProps = {};
    if (allowMultiSelect) {
        const checkboxStates = [JobStatus.SUCCEEDED, JobStatus.FAILED];
        multiLoadProps = {
            checkboxSelection: true,
            isRowSelectable: (params) => checkboxStates.includes(params.row.state),
            onRowSelectionModelChange: (model) => {
                // MUI X DataGrid v9's row selection model is { type, ids: Set } rather than a
                // plain id array. "select all" switches to 'exclude' mode with an empty `ids`
                // (nothing excluded, i.e. everything selected), so it must be resolved against
                // the full row list rather than spread directly.
                const selectedIds = model.type === 'exclude'
                    ? rows.map((r) => r.id).filter((id) => !model.ids.has(id))
                    : [...model.ids];
                setCheckedRowItems(selectedIds);
            },
        };
    }

    // Message displayed by datagrid when there are no rows
    const localeText = { noRowsLabel: 'No jobs' };

    const loadButtonDisabled = jobs.filter((job) => (
        checkedRowItems.includes(job.jobid)
        && !localJobState[job.jobid]?.error
    )).length === 0;

    const removeButtonDisabled = checkedRowItems.length === 0;

    function handleColumnWidthChange(params) {
        const newWidths = { ...columnWidths, [params.colDef.field]: params.width };
        setColumnWidths(newWidths);
    }

    return (
        <>
            <DataGrid
                apiRef={apiRef}
                rows={rows}
                columns={columns}
                disableColumnMenu={false}
                disableRowSelectionOnClick
                initialState={{
                    pagination: {
                        paginationModel: { pageSize: 10, page: 0 },
                    },
                    density: 'comfortable',
                }}
                pageSizeOptions={[10, 25, 50]}
                getRowClassName={(params) => (params.id === flashId ? 'flash' : '')}
                localeText={localeText}
                slotProps={{
                    basePopper: { placement: 'bottom-start' },
                }}
                sx={{
                    // Header cells can be made bold with this,
                    // but it would be inconsistent with the other DataGrids, so deferring.
                    // '& .MuiDataGrid-columnHeaderTitle': {
                    //     fontWeight: 'bold',
                    // },
                    '& .flash': {
                        animation: 'flashBg 0.5s ease-in-out',
                    },
                    '@keyframes flashBg': {
                        '0%, 60%': { backgroundColor: 'lightblue' },
                        '100%': { backgroundColor: 'transparent' },
                    },
                }}
                onColumnWidthChange={handleColumnWidthChange}
                {...multiLoadProps}
                {...dataGridProps}
            />
            {(errors.length > 0) && <ImportErrors errors={errors} /> }
            { allowMultiSelect && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexWrap: 'wrap',
                    gap: '1rem',
                    paddingTop: '1rem',
                    width: window.screen.width > 1000 ? '14rem' : '100%',
                }}
                >
                    <BMapsTooltip title={loadButtonDisabled ? 'Select a Job to Load Results' : 'Load Selected Job Results'}>
                        {wrapForDisabledTooltip(
                            <DialogPrimaryButton
                                type="button"
                                variant="contained"
                                sx={{ width: '100%' }}
                                onClick={() => {
                                    integrateJobResults(checkedRowItems);
                                }}
                                disabled={loadButtonDisabled}
                            >
                                Load Job Results
                            </DialogPrimaryButton>
                        )}
                    </BMapsTooltip>
                    <BMapsTooltip title={removeButtonDisabled ? 'Select a Job to Remove' : 'Remove Selected Job Results'}>
                        {wrapForDisabledTooltip(
                            <DialogCancelButton
                                type="button"
                                sx={{ width: '100%' }}
                                onClick={() => {
                                    deleteJobs(checkedRowItems);
                                }}
                                disabled={removeButtonDisabled}
                            >
                                Remove Job Results
                            </DialogCancelButton>
                        )}
                    </BMapsTooltip>
                </Box>
            )}
        </>
    );
}

/**
 * Takes the job data and builds the columns and rows of the datagrid
 * @param {Job[]} jobs
 * @param {{ [string]: { [key: string]: any } }} localJobState
 */
function buildJobsGrid(
    jobs, getErrorMessage, integrateJobResults, deleteJobs, columnControl, columnWidths
) {
    // building the columns and rows for the data grid
    const columnNames = getColumnNames(jobs, columnControl);
    const columns = getColumns(
        columnNames, getErrorMessage, integrateJobResults, deleteJobs,
        columnWidths,
    );

    // places most recent job first, replace with timestamp once available.
    const rows = jobs
        .map((job) => {
            const jobRow = {};
            for (const col of expectedColumns) {
                jobRow[col.field] = col.valueGetter(job);
            }
            return ({ ...jobRow, id: job.jobid });
        })
        .sort((a, b) => {
            try {
                const sort = b.created.localeCompare(a.created);
                if (sort === 0) return b.id.localeCompare(a.id);
                return sort;
            } catch {
                return b.id.localeCompare(a.id);
            }
        });

    return { columns, rows };
}

/**
 *
 * @param {import('BMapsRedux/jobs/slice').Job[]} jobs
 * @returns
 */
function getColumnNames(jobs, columnControl={}) {
    const columnNames = ['jobName', 'created', 'jobtype', 'state'];
    for (const job of jobs) {
        for (const column of expectedColumns) {
            if (columnControl.exclude?.includes(column.field)) continue;
            if (column.valueGetter(job) && !columnNames.includes(column.field)) {
                columnNames.push(column.field);
            }
        }
    }

    return columnNames;
}

function getColumns(
    columnNames, getErrorMessage, integrateJobResults, deleteJobs,
    columnWidths,
) {
    const columns = expectedColumns.filter((col) => columnNames.includes(col.field))
        .map((col) => {
            const column = {
                field: col.field,
                headerName: col.headerName,
                width: columnWidths[col.field],
                flex: columnWidths[col.field] ? undefined : 1,
                minWidth: 100,
            };

            switch (col.field) {
                case 'actions':
                    column.width = undefined;
                    column.flex = 1;
                    column.minWidth = 75;
                    column.sortable = false;
                    column.filterable = false;
                    column.align = 'right';
                    column.headerAlign = 'right';
                    column.renderCell = ({ row }) => {
                        const loadActionStates = [JobStatus.SUCCEEDED];
                        const removeActionStates = [JobStatus.SUCCEEDED, JobStatus.FAILED];

                        const jobTypeName = lookupJobDefinition(row.jobtype)?.getJobTypeName();
                        let noun = `${jobTypeName} Result`;
                        if (Loader.AllowLabFeatures) {
                            noun += ` (${row.id})`;
                        }
                        const actions = [];

                        if (loadActionStates.includes(row.state)) {
                            actions.push(
                                <BMapsTooltip title={`Load ${noun}`}>
                                    <IconButton
                                        size="small"
                                        onClick={() => integrateJobResults([row.id])}
                                    >
                                        <AddCircleOutlineIcon fontSize="inherit" />
                                    </IconButton>
                                </BMapsTooltip>
                            );
                        }
                        if (removeActionStates.includes(row.state)) {
                            actions.push(
                                <BMapsTooltip title={`Remove ${noun}`}>
                                    <IconButton
                                        size="small"
                                        onClick={() => deleteJobs([row.id])}
                                    >
                                        <ClearIcon fontSize="inherit" />
                                    </IconButton>
                                </BMapsTooltip>
                            );
                        }
                        return (
                            <Box>
                                {actions.map((a) => (
                                    // Actions are BMapsTooltips with unique titles
                                    <React.Fragment key={a.props.title}>{a}</React.Fragment>
                                ))}
                            </Box>
                        );
                    };
                    break;
                case 'error':
                    column.description = 'errors';
                    column.renderCell = ({ value, row }) => {
                        if (!value) {
                            return null;
                        }
                        if (value === 'load error') {
                            let tooltip = 'Errors created while processing job. Click to display error information.';
                            if (Loader.AllowLabFeatures) {
                                tooltip += ` (job id ${row.id})`;
                            }
                            return (
                                <BMapsTooltip title={tooltip}>
                                    <Button
                                        onClick={() => getErrorMessage(row.id)}
                                        aria-label="Populate Error Text"
                                        variant="outlined"
                                        size="small"
                                        color="error"
                                    >
                                        Load Error
                                    </Button>
                                </BMapsTooltip>
                            );
                        } else {
                            return (
                                <BMapsTooltip title={value}>
                                    <span style={{
                                        display: 'block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                    >
                                        {value}
                                    </span>
                                </BMapsTooltip>
                            );
                        }
                    };
                    break;
                case 'delivered':
                case 'created':
                    column.renderCell = ({ value, row }) => {
                        if (!value) return null;
                        const datetime = new Date(value).toLocaleString('en-US', {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                        });
                        const date = new Date(value).toLocaleString('en-US', {
                            dateStyle: 'short',
                        });
                        let tooltip = datetime;
                        if (Loader.AllowLabFeatures) {
                            tooltip += ` (job id ${row.id})`;
                        }
                        return (
                            <BMapsTooltip title={tooltip}>
                                <span style={{
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                                >
                                    {date}
                                </span>
                            </BMapsTooltip>
                        );
                    };
                    break;
                default:
                    break;
            }
            return column;
        });
    return columns;
}

const expectedColumns = [
    {
        field: 'jobName',
        headerName: 'Name',
        valueGetter: (job) => job.requestmeta?.jobName,
    },
    {
        field: 'moleculeName',
        headerName: 'System Name',
        valueGetter: (job) => job.requestmeta?.moleculeName,
    },
    {
        field: 'created',
        headerName: 'Start Date',
        valueGetter: (job) => job.created,
    },
    {
        field: 'jobtype',
        headerName: 'Job Type',
        valueGetter: (job) => job.jobtype,
    },
    {
        field: 'foldingProgram',
        headerName: 'Program',
        valueGetter: (job) => job.requestmeta?.foldingProgram,
    },
    {
        field: 'state',
        headerName: 'State',
        valueGetter: (job) => job.state,
    },
    {
        field: 'delivered',
        headerName: 'Last Viewed',
        valueGetter: (job) => job.delivered,
    },
    {
        field: 'error',
        headerName: 'Errors',
        valueGetter: (job) => {
            const { localJobState } = getJobState();
            if (localJobState[job.jobid]?.error) {
                return localJobState[job.jobid].error;
            }
            if (job.state === 'failed') {
                return 'load error';
            }
            return null;
        },
    },
    {
        field: 'actions',
        headerName: ' ',
        valueGetter: () => true,
    },
];
